import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

const DELIVERY_LABELS = { VET:'VET Express', JT_COD:'J&T Express (COD)', JT_NON_COD:'J&T Express', DOM:'DOM Delivery', SONA:'Sona Delivery', TAKE_SHOP:'Take Shop', GRAB:'Grab' };
const PROVINCE = ['VET','JT_NON_COD'];

export default function BillPrint() {
  const [bill, setBill] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchParams] = useSearchParams();
  useEffect(()=>{
    const billId = searchParams.get('bill_id');
    if (!billId) { setError('No bill ID provided — URL: '+window.location.href); setLoading(false); return; }
    Promise.all([
      api.get(`/billing/bills/${billId}`),
      api.get('/settings/public')
    ]).then(([br, sr])=>{
      setBill(br.data);
      setSettings(sr.data||{});
      setLoading(false);
      setTimeout(()=>window.print(), 800);
    }).catch((err)=>{ setError('Failed to load bill: '+(err?.response?.status||'')+(err?.message||'')+' token='+(localStorage.getItem('token')||'NONE')); setLoading(false); });
  },[]);

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'#6b7280' }}>Loading bill...</div>;
  if (error) return <div style={{ padding:40, textAlign:'center', color:'#dc2626' }}>{error}</div>;
  if (!bill) return null;

  const isProvince = PROVINCE.includes(bill.preferred_delivery);
  const phones = bill.customer_phones?.length ? bill.customer_phones : bill.customer_phone ? [bill.customer_phone] : [];

  return (
    <div>
      <style>{`
        @media print {
          body { margin: 0; }
          .no-print { display: none !important; }
          @page { size: A5; margin: 8mm; }
        }
        body { font-family: 'Khmer OS', Arial, sans-serif; background: #fff; }
        .bill-wrap { width: 148mm; margin: 0 auto; padding: 8mm; box-sizing: border-box; }
        .bill-number { font-size: 22px; font-weight: 900; font-family: monospace; letter-spacing: 2px; color: #000; }
        .divider { border: none; border-top: 1px dashed #999; margin: 8px 0; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th { text-align: left; font-size: 10px; color: #666; padding: 3px 4px; border-bottom: 1px solid #eee; }
        td { padding: 4px; font-size: 11px; }
        .total-row td { font-weight: 700; font-size: 13px; border-top: 1px solid #000; padding-top: 6px; }
      `}</style>

      <div className="no-print" style={{ padding:16, background:'#f3f4f6', display:'flex', gap:12, alignItems:'center' }}>
        <button onClick={()=>window.print()} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'10px 24px', fontSize:14, fontWeight:600, cursor:'pointer' }}>🖨️ Print</button>
        <button onClick={()=>window.close()} style={{ background:'#f3f4f6', color:'#374151', border:'1px solid #d1d5db', borderRadius:8, padding:'10px 20px', fontSize:14, cursor:'pointer' }}>Close</button>
        <span style={{ fontSize:13, color:'#6b7280' }}>A5 paper — {bill.bill_number}</span>
      </div>

      <div className="bill-wrap">
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:900, color:'#7c3aed' }}>{settings.business_name||'123 Crystal'}</div>
            {settings.business_phone&&<div style={{ fontSize:11, color:'#666' }}>📞 {settings.business_phone}</div>}
            <div style={{ fontSize:10, color:'#999', marginTop:2 }}>{bill.created_at}</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:10, color:'#666', marginBottom:2 }}>BILL NUMBER</div>
            <div className="bill-number">{bill.bill_number}</div>
            {bill.tracking_number&&<div style={{ fontSize:10, color:'#666', marginTop:4 }}>📦 {bill.tracking_number}</div>}
          </div>
        </div>

        <hr className="divider" />

        {/* Customer */}
        <div style={{ marginBottom:10 }}>
          <div style={{ fontSize:10, color:'#666', textTransform:'uppercase', marginBottom:4 }}>Customer</div>
          <div style={{ fontSize:14, fontWeight:700, color:'#000' }}>{bill.customer_name}</div>
          {phones.map((p,i)=><div key={i} style={{ fontSize:11, color:'#333' }}>📞 {p}</div>)}
          {bill.customer_address&&<div style={{ fontSize:11, color:'#333' }}>📍 {bill.customer_address}</div>}
          {bill.preferred_delivery&&<div style={{ fontSize:11, color:'#7c3aed', marginTop:2 }}>🚚 {DELIVERY_LABELS[bill.preferred_delivery]||bill.preferred_delivery}</div>}
        </div>

        <hr className="divider" />

        {/* Orders */}
        <table style={{ marginBottom:10 }}>
          <thead>
            <tr>
              <th>Order No</th>
              <th>Product</th>
              <th style={{ textAlign:'center' }}>Qty</th>
              <th style={{ textAlign:'right' }}>Price</th>
              <th style={{ textAlign:'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {bill.orders?.map(o=>(
              <tr key={o.id}>
                <td style={{ fontFamily:'monospace', fontSize:9, color:'#666' }}>{o.order_number}</td>
                <td>{o.product_code||'—'}</td>
                <td style={{ textAlign:'center' }}>{o.quantity}</td>
                <td style={{ textAlign:'right' }}>${o.unit_price?.toFixed(2)}</td>
                <td style={{ textAlign:'right', fontWeight:600 }}>${o.total?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <hr className="divider" />

        {/* Totals */}
        <table style={{ marginBottom:10 }}>
          <tbody>
            <tr>
              <td style={{ color:'#666' }}>Subtotal</td>
              <td style={{ textAlign:'right' }}>${parseFloat(bill.subtotal||0).toFixed(2)}</td>
            </tr>
            {parseFloat(bill.delivery_fee||0)>0&&<tr>
              <td style={{ color:'#666' }}>Delivery</td>
              <td style={{ textAlign:'right' }}>+${parseFloat(bill.delivery_fee).toFixed(2)}</td>
            </tr>}
            {parseFloat(bill.discount||0)>0&&<tr>
              <td style={{ color:'#dc2626' }}>Discount</td>
              <td style={{ textAlign:'right', color:'#dc2626' }}>-${parseFloat(bill.discount).toFixed(2)}</td>
            </tr>}
            <tr className="total-row">
              <td>TOTAL</td>
              <td style={{ textAlign:'right', fontSize:16, color:'#7c3aed' }}>${parseFloat(bill.total||0).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        {/* Province payment info */}
        {isProvince&&settings.aba_account_name&&(
          <>
            <hr className="divider" />
            <div style={{ background:'#faf5ff', border:'1px solid #ddd6fe', borderRadius:6, padding:'8px 12px', marginBottom:10 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#7c3aed', marginBottom:4 }}>💳 Payment Info</div>
              <div style={{ fontSize:11, color:'#333' }}>ABA: <strong>{settings.aba_account_name}</strong></div>
              {settings.aba_account_number&&<div style={{ fontSize:11, color:'#333' }}>Account: <strong>{settings.aba_account_number}</strong></div>}
            </div>
          </>
        )}

        {bill.staff_note&&(
          <>
            <hr className="divider" />
            <div style={{ fontSize:11, color:'#666' }}>📝 {bill.staff_note}</div>
          </>
        )}

        <hr className="divider" />
        <div style={{ fontSize:10, color:'#999', textAlign:'center' }}>
          Thank you for your purchase! 🙏 123 Crystal
        </div>
      </div>
    </div>
  );
}
